import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import MicIcon from "@mui/icons-material/Mic";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Avatar from "@mui/material/Avatar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import SimpleBlogCard from "parts/Cards/BlogCards/SimpleBlogCard";

import learningImage from "assets/images/ai/LearningSupportAI.png";
import careerImage from "assets/images/ai/CareerCounselingAI.png";
import mentalImage from "assets/images/ai/MentalHealthAI.png";
import lifehackImage from "assets/images/ai/LifehackAI.png";
import techImage from "assets/images/ai/TechnicalQuestionAI.png";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";

import { usePaywall } from "context/PaywallContext";

import VoiceWave from "components/ai/talk/VoiceWave";
import useVoiceInput from "hooks/useVoiceInput";

import { postAITalk, playAudioStreamAuto, fetchAITalkStatus } from "@shared/api/aiTalk";

import { useNotification } from "context/NotificationContext";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles"

// 画像がオブジェクト（Next.js等）でインポートされた場合でも確実にURLを取り出すヘルパー関数
const getValidImageSrc = (img: any): string | undefined => {
  if (!img) return undefined;
  if (typeof img === "string") return img;
  return img.src || img.default?.src || undefined;
};

// 型エラーを防ぐため image を any に変更
type ChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  agentImage?: any;
};

type AIAgent = {
  label: string;
  value: string;
  description: string;
  detail: string;
  image: any;
};

function Bubble({
  role,
  text,
  agentImage,
  pending,
  statusText,
}: {
  role: "user" | "assistant" | "system";
  text: string;
  agentImage?: any;
  pending?: boolean;
  statusText?: string;
}) {
  const isUser = role === "user";
  const isSystem = role === "system";

  if (isSystem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <Box sx={{ bgcolor: 'rgba(0,0,0,0.05)', px: 2, py: 0.5, borderRadius: 2 }}>
          <MDTypography variant="caption" color="text">
            {text}
          </MDTypography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        mb: 2,
        gap: 1
      }}
    >
      {!isUser && (
        // 安全に抽出した画像URLをAvatarに渡すことで、正しく画像が表示されます
        <Avatar src={getValidImageSrc(agentImage)} alt="AI" sx={{ width: 36, height: 36, mb: 0.5 }}>AI</Avatar>
      )}

      <Box
        sx={{
          maxWidth: '80%',
          bgcolor: isUser ? '#3b82f6' : '#ffffff',
          color: isUser ? '#ffffff' : 'inherit',
          p: 1.5,
          borderRadius: 2,
          borderBottomRightRadius: isUser ? 0 : 2,
          borderTopLeftRadius: !isUser ? 0 : 2,
          border: !isUser ? '1px solid #e0e0e0' : 'none',
          boxShadow: !isUser ? 1 : 0
        }}
      >
        {pending && !text ? (
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
              py: '4px',
              '@keyframes typingBlink': {
                '0%': { opacity: 0.2 },
                '20%': { opacity: 1 },
                '100%': { opacity: 0.2 },
              },
              '& .dot': {
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'rgba(0,0,0,0.4)',
                animation: 'typingBlink 1.4s infinite both',
              },
              '& .dot:nth-of-type(2)': { animationDelay: '0.2s' },
              '& .dot:nth-of-type(3)': { animationDelay: '0.4s' },
            }}
          >
            <Box className="dot" />
            <Box className="dot" />
            <Box className="dot" />
            {statusText && (
              <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                {statusText}
              </MDTypography>
            )}
          </Box>
        ) : (
          <MDTypography
            variant="body2"
            color={isUser ? "white" : "text"}
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {text}
          </MDTypography>
        )}
      </Box>
    </Box>
  );
}

function AITalk() {
  const { showNotification } = useNotification();
  const [selectedAgent, setSelectedAgent] = useState("learning");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [streamStatus, setStreamStatus] = useState("");
  const [openDetail, setOpenDetail] = useState(false);
  const [detailAgent, setDetailAgent] = useState<AIAgent | null>(null);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { t } = useContext(LanguageContext) as any;
  const { session } = useSessionContext();

  const { openPaywall } = usePaywall();

  const [isScreenLimited, setIsScreenLimited] = useState(false);
  const [limitStatusText, setLimitStatusText] = useState("");
  const [limitModalVisible, setLimitModalVisible] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");

  const { isListening, result, interimText, error, startListening, stopListening } =
    useVoiceInput();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const aiAgents = [
    {
      label: t("Agents.learning.label"),
      value: "learning",
      description: t("Agents.learning.description"),
      detail: t("Agents.learning.detail"),
      image: learningImage,
    },
    {
      label: t("Agents.career.label"),
      value: "career",
      description: t("Agents.career.description"),
      detail: t("Agents.career.detail"),
      image: careerImage,
    },
    {
      label: t("Agents.mental.label"),
      value: "mental",
      description: t("Agents.mental.description"),
      detail: t("Agents.mental.detail"),
      image: mentalImage,
    },
    {
      label: t("Agents.lifehack.label"),
      value: "lifehack",
      description: t("Agents.lifehack.description"),
      detail: t("Agents.lifehack.detail"),
      image: lifehackImage,
    },
    {
      label: t("Agents.tech.label"),
      value: "tech",
      description: t("Agents.tech.description"),
      detail: t("Agents.tech.detail"),
      image: techImage,
    },
  ];

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "init",
      role: "assistant",
      content: t("AITalk.initial-message"),
      agentImage: aiAgents[0].image
    }
  ]);

  const checkLimit = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const status = await fetchAITalkStatus(session.access_token);
      setIsScreenLimited(status.isLimitExceeded);
      if (status.isLimitExceeded) {
        const statusText = t("AITalk.limit_reached_status").replace("{maxChats}", String(status.maxChats));
        setLimitStatusText(statusText);
      } else {
        setLimitStatusText("");
      }
    } catch (e) {
      console.error("[AiTalk] Status Fetch Error:", e);
    }
  }, [session?.access_token, t]);

  useEffect(() => {
    checkLimit();
  }, [checkLimit]);

  useEffect(() => {
    setMessages((prevMessages) => {
      if (prevMessages.length > 0 && prevMessages[0].id === "init") {
        const updatedMessages = [...prevMessages];
        updatedMessages[0] = {
          ...updatedMessages[0],
          content: t("AITalk.initial-message")
        };
        return updatedMessages;
      }
      return prevMessages;
    });
  }, [t]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (result) {
      setQuestion((prev) => (prev ? prev + " " + result : result));
    }
  }, [result]);

  const handleSubmit = async () => {
    if (!question.trim() || isScreenLimited || loading) return;

    setLoading(true);
    // 万が一見つからない場合は初期エージェントをフォールバック
    const currentAgent = aiAgents.find(a => a.value === selectedAgent) || aiAgents[0];
    const userMsg: ChatMessage = { id: Date.now().toString(), role: "user", content: question };

    const assistantId = (Date.now() + 1).toString();
    setMessages(prev => [
      ...prev,
      userMsg,
      { id: assistantId, role: "assistant", content: "", agentImage: currentAgent.image },
    ]);
    setStreamingId(assistantId);
    setStreamStatus(t("AITalk.generating"));

    const currentQuestion = question;
    setQuestion("");

    try {
      const data = await postAITalk(
        session.access_token,
        selectedAgent,
        currentQuestion,
        (_chunk, accumulated) => {
          setMessages(prev =>
            prev.map(m => (m.id === assistantId ? { ...m, content: accumulated } : m))
          );
        },
        (status) => setStreamStatus(status.message)
      );

      const answer = data.answer ?? t("AITalk.error.network");
      setMessages(prev =>
        prev.map(m => (m.id === assistantId ? { ...m, content: answer } : m))
      );

      if (voiceEnabled && data.chat_historiesId && audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.load();
          await playAudioStreamAuto(session.access_token, selectedAgent, data.chat_historiesId, audioRef.current);
        } catch (err) {
          showNotification({
            icon: "error",
            title: t("Common.error"),
            message: t("AITalk.error.play"),
          }, 2000);
        }
      }
      await checkLimit();
    } catch (error: any) {
      console.error("送信エラー:", error);

      setMessages(prev => prev.filter(m => m.id !== assistantId));

      if (error.isLimitExceeded || error.response?.data?.isLimitExceeded) {
        const errMsg = error.message || error.response?.data?.message || t("AITalk.limit_reached_fallback");
        setLimitMessage(errMsg);
        setLimitModalVisible(true);
        setIsScreenLimited(true);
        setLimitStatusText(errMsg);

        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: "system",
          content: errMsg
        }]);
      } else {
        const message = error?.retryable && error?.message
          ? error.message
          : t("AITalk.error.generic");
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: "system",
          content: message
        }]);
      }
    } finally {
      setLoading(false);
      setStreamingId(null);
      setStreamStatus("");
    }
  };

  const handleChange = (
    e: React.ChangeEvent<globalThis.HTMLInputElement | globalThis.HTMLTextAreaElement>
  ) => {
    setQuestion(e.target.value);
  };

  const openAgentDetail = (agent: AIAgent) => {
    setDetailAgent(agent as any);
    setOpenDetail(true);
  };

  const closeDetailDialog = () => setOpenDetail(false);

  const handleCopy = () => {
    const lastAssistantMsg = [...messages].reverse().find(m => m.role === "assistant");
    if (!lastAssistantMsg) return;

    navigator.clipboard.writeText(lastAssistantMsg.content).then(() => {
      showNotification({
        icon: "info",
        title: t("Common.success"),
        message: t("AITalk.copy-success"),
      }, 2000);
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={12}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDTypography variant="h4" mb={2}>
              {t("AITalk.title")}
            </MDTypography>

            <Grid container spacing={1}>
              {aiAgents.map((agent) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={agent.value}>
                  <MDBox
                    onClick={() => {
                        setSelectedAgent(agent.value);
                        setMessages(prev => [...prev, {
                            id: Date.now().toString(),
                            role: "system",
                            content: `${agent.label} ${t("AITalk.switched")}`
                        }]);
                    }}
                    sx={{ cursor: "pointer", display: "flex", flexDirection: "column", height: "100%" }}
                  >
                    <SimpleBlogCard
                      // ここでも安全に抽出したURLを渡すことで、上部のカード画像割れも直ります
                      image={getValidImageSrc(agent.image)}
                      title={agent.label}
                      description={agent.description}
                      action={{
                        type: "internal",
                        route: "#",
                        label: t("AITalk.detail"),
                        color: "info",
                        onClick: () => {
                          openAgentDetail(agent);
                        },
                      }}
                      sx={{ flexGrow: 1, m: 2 }}
                    />
                    {selectedAgent === agent.value && (
                      <MDBox mt={isMobile ? 1 : 0.5} sx={{ width: "100%", textAlign: "center" }}>
                        <MDTypography color="info" fontWeight="bold">
                          {t("AITalk.selected")}                      
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </Grid>
              ))}
            </Grid>

            {/* チャットエリア */}
            <Card sx={{ backgroundColor: "#f0f2f5", position: "relative", minHeight: "400px", mt: 2, display: 'flex', flexDirection: 'column' }}>
              <MDBox
                p={2}
                sx={{
                  flexGrow: 1,
                  maxHeight: "500px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {voiceEnabled && <audio ref={audioRef} hidden controls />}

                {/* メッセージリスト表示 */}
                {messages.map((msg) => (
                    <Bubble
                        key={msg.id}
                        role={msg.role}
                        text={msg.content}
                        agentImage={msg.agentImage}
                        pending={msg.id === streamingId && msg.content.length === 0}
                        statusText={msg.id === streamingId ? streamStatus : undefined}
                    />
                ))}

                <div ref={messagesEndRef} />
              </MDBox>

              {/* 制限到達時のバナー表示 */}
              {isScreenLimited && (
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  px={3}
                  py={1.5}
                  sx={{ backgroundColor: "#fffbeb", borderTop: "1px solid #fde68a" }}
                >
                  <MDTypography variant="button" fontWeight="bold" sx={{ color: "#b45309" }}>
                    {limitStatusText}
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ color: "#f59e0b", cursor: "pointer", textDecoration: "underline" }}
                    onClick={openPaywall}
                  >
                    {t("AITalk.upgrade_pro")}
                  </MDTypography>
                </MDBox>
              )}

              {/* 入力エリア */}
              <MDBox p={2} bgcolor="white" sx={{ borderTop: '1px solid #e0e0e0', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                 <MDBox display="flex" alignItems="center" mb={1} gap={1}>
                    <FormControlLabel
                        control={
                        <Switch checked={voiceEnabled} onChange={() => setVoiceEnabled(!voiceEnabled)} />
                        }
                        label={t("AITalk.play-title")}
                        sx={{ m: 0 }}
                    />
                    <Tooltip title={t("AITalk.copy")}>
                        <IconButton size="medium" onClick={handleCopy}>
                        <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                 </MDBox>

                 <MDInput
                    label={t("AITalk.questionInput.label")}
                    value={question}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={isScreenLimited ? t("AITalk.limit_reached_placeholder") : ""}
                    disabled={isScreenLimited || loading}
                    sx={{
                      "& .MuiInputBase-root": {
                        paddingTop: "14px", // 上部の文字切れを防ぐ
                        paddingBottom: "14px",
                      },
                      "& .MuiInputBase-input": {
                        lineHeight: "1.6",
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        // 日本語変換中（IME入力中）のEnterは無視する
                        if (e.nativeEvent.isComposing) return;

                        if (e.key === 'Enter') {
                            if (e.shiftKey) {
                                return;
                            } else {
                                e.preventDefault(); 
                                if (!loading && question.trim() && !isScreenLimited) {
                                    handleSubmit();
                                }
                            }
                        }
                    }}
                 />
                 
                 {isListening && interimText && (
                    <Box sx={{ p: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                        {interimText}
                    </Box>
                 )}

                 {isListening && <VoiceWave active={isListening} />}

                 {/* 右下の設定ボタン（歯車アイコン）と重ならないように pr（右パディング）を追加 */}
                 <MDBox mt={2} display="flex" alignItems="center" justifyContent="space-between" pr={{ xs: 0, md: 8 }}>
                    <Tooltip
                      title={isListening ? t("AITalk.voice-title1") : t("AITalk.voice-title2")}
                    >
                      <span>
                        <IconButton 
                          onClick={isListening ? stopListening : startListening} 
                          disabled={isScreenLimited}
                        >
                          <MicIcon color={isListening ? "error" : (isScreenLimited ? "disabled" : "primary")} />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleSubmit}
                      disabled={loading || !question.trim() || isScreenLimited}
                    >
                      <Icon>send</Icon>&nbsp;{t("AITalk.submit")}
                    </MDButton>
                 </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ポップアップ詳細ダイアログ */}
      <Dialog 
        open={openDetail} 
        onClose={closeDetailDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {detailAgent?.label}
          {t("AITalk.dialog.title")}
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={1}>
            <MDTypography 
              variant="body2" 
              color="dark"
              sx={{ 
                lineHeight: 1.8,
                letterSpacing: '0.02em',
                fontSize: '0.9rem',
                color: '#000000',
              }}
            >
              {detailAgent?.detail && detailAgent.detail
                .replace(/。/g, "。\n")
                .split("\n")
                .filter((line: string) => line.trim() !== "")
                .map((line: string, index: number) => (
                  <span 
                    key={index} 
                    style={{ 
                      display: "block", 
                      marginBottom: "16px" 
                    }}
                  >
                    {line}
                  </span>
                ))}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="info" variant="gradient" onClick={closeDetailDialog}>
            {t("AITalk.dialog.close")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* 上限到達 ＆ Paywall誘導モーダル */}
      <Dialog 
        open={limitModalVisible} 
        onClose={() => setLimitModalVisible(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
          {t("AITalk.limit_modal_title")}
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={1} textAlign="center">
            <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
              {limitMessage}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, p: 2 }}>
          <MDButton
            color="warning"
            variant="gradient"
            fullWidth
            onClick={() => {
              setLimitModalVisible(false);
              openPaywall();
            }}
          >
            {t("AITalk.upgrade_pro")}
          </MDButton>
          <MDButton
            color="light"
            variant="contained"
            fullWidth
            onClick={() => setLimitModalVisible(false)}
          >
            {t("AITalk.later")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AITalk;
